import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const comprehensiveHealthPanel: ContentBlock<"module"> = {
  id: makeId("module-comprehensive-wellness"),
  kind: "module",
  tags: [
    "both-partners",
    "assessment",
    "testing",
    "metabolic",
    "autoimmune",
    "nutritional",
  ],
  title: "Comprehensive Health Panel",
  subtitle:
    "100+ biomarkers in a single draw — metabolic, hormonal, inflammatory, autoimmune, nutritional, and more",
  icon: "🩸",
  body: {
    duration: "1–2 weeks",
    category: "assessment",
    forWhom: "both",
    includes: [
      // --- Partner lab panel (100+ markers) ---
      "Full hormonal panel — FSH, LH, estradiol, prolactin, DHEA-S, total and free testosterone, SHBG, cortisol",
      "Complete thyroid — TSH, free T3, free T4, TPO antibodies, thyroglobulin antibodies",
      "Metabolic health — fasting glucose, fasting insulin, HbA1c, uric acid, triglyceride-glucose index",
      "Advanced lipids — total cholesterol, LDL, HDL, triglycerides, ApoB, lipoprotein(a), LDL particle count and size",
      "Inflammation — hs-CRP, homocysteine, ESR, ferritin",
      "Nutrients — Vitamin D, B12, folate, iron studies, zinc, magnesium, omega-3 index",
      "Liver function — ALT, AST, GGT, bilirubin, albumin, alkaline phosphatase",
      "Kidney function — creatinine, BUN, eGFR, electrolytes",
      "Complete blood count — RBC, WBC, platelets, hemoglobin, hematocrit, differential",
      "Autoimmune screening — ANA, anti-thyroid antibodies",
      "IGF-1 and additional longevity markers",
      // --- Fertility-specific add-ons (not in partner panels) ---
      "Antiphospholipid antibodies — anticardiolipin, lupus anticoagulant, anti-β2 glycoprotein (recurrent loss screening)",
      "Coagulation genetics — Factor V Leiden, prothrombin gene mutation, MTHFR C677T/A1298C",
      "Mid-luteal progesterone (cycle day ~21) to confirm ovulation",
      "Infectious disease screen — rubella immunity, varicella, hepatitis B/C, HIV, CMV",
      "Results synthesis with a fertility-specialized provider connecting all 100+ markers to your reproductive plan",
    ],
    trackableMarkers: [
      // --- Immediately actionable for fertility ---
      {
        name: "Fasting insulin",
        expectedToChange: true,
        expectation:
          "Insulin resistance is common with PMOS (ex PCOS) and directly impacts egg quality. Responds to diet, exercise, inositol, or metformin within 2–3 months.",
        retestInterval: "3 months",
      },
      {
        name: "HbA1c",
        expectedToChange: true,
        expectation:
          "Reflects 3-month average blood sugar. Goal is <5.7%. Responds to dietary changes and blood sugar management.",
        retestInterval: "3 months",
      },
      {
        name: "hs-CRP",
        expectedToChange: true,
        expectation:
          "Systemic inflammation marker linked to implantation failure and pregnancy complications. Responds to anti-inflammatory diet and omega-3s within 8–12 weeks.",
        retestInterval: "3 months",
      },
      {
        name: "Homocysteine",
        expectedToChange: true,
        expectation:
          "Elevated levels linked to recurrent miscarriage and neural tube defects. Responds well to methylfolate, B12, and B6 supplementation.",
        retestInterval: "8 weeks",
      },
      {
        name: "Vitamin D",
        expectedToChange: true,
        expectation:
          "Deficiency linked to poorer IVF outcomes and pregnancy complications. Goal is >40 ng/mL. Correctable with supplementation in 8 weeks.",
        retestInterval: "8 weeks",
      },
      {
        name: "Ferritin",
        expectedToChange: true,
        expectation:
          "Iron stores affect energy, egg quality, and pregnancy health. Goal is >40 ng/mL. Responds to supplementation over 3–6 months.",
        retestInterval: "3 months",
      },
      {
        name: "TSH",
        expectedToChange: true,
        expectation:
          "Fertility goal is <2.5 mIU/L. If elevated or if TPO antibodies are positive, medication typically corrects within 6–8 weeks.",
        retestInterval: "6–8 weeks",
      },
      {
        name: "Prolactin",
        expectedToChange: true,
        expectation:
          "Elevated prolactin can suppress ovulation. Treatable with cabergoline. Levels normalize within weeks of treatment.",
        retestInterval: "4–6 weeks",
      },
      // --- Important baselines (don't change, but inform treatment) ---
      {
        name: "DHEA-S",
        expectedToChange: false,
        expectation:
          "Low DHEA-S in women with diminished ovarian reserve may prompt supplementation. Important baseline for IVF protocol planning.",
      },
      {
        name: "Antiphospholipid antibodies",
        expectedToChange: false,
        expectation:
          "Screens for antiphospholipid syndrome — a leading cause of recurrent miscarriage. If positive, requires confirmation at 12 weeks and may warrant aspirin/heparin during pregnancy.",
      },
      {
        name: "Factor V Leiden / MTHFR",
        expectedToChange: false,
        expectation:
          "Genetic thrombophilia screening. Doesn't change, but informs whether blood thinners are needed during pregnancy and IVF cycles.",
      },
      {
        name: "ApoB",
        expectedToChange: true,
        expectation:
          "Better predictor of cardiovascular risk than LDL alone. Important baseline before pregnancy, which significantly stresses the cardiovascular system.",
        retestInterval: "6 months",
      },
    ],
    priceRange: { low: 500, high: 2000 },
    currency: "USD",
    status: "coming-soon",
    steps: [
      {
        title: "Fasting blood draw",
        description:
          "All 100+ biomarkers collected in a single morning fasting draw via our lab partner. We coordinate everything — one visit, one draw.",
      },
      {
        title: "Mid-luteal progesterone",
        description:
          "Separate draw 7 days after ovulation (cycle day ~21) to confirm ovulatory function. Quick lab visit or at-home kit.",
      },
      {
        title: "Fertility-specific interpretation",
        description:
          "45-minute session with a fertility-specialized provider. We don't just give you 100 numbers — we connect every marker to your reproductive plan.",
      },
    ],
  },
  sources: [
    {
      title:
        "Antiphospholipid syndrome and recurrent pregnancy loss: ASRM committee opinion",
      journal: "Fertility and Sterility",
      year: 2022,
      evidenceLevel: "expert-opinion",
    },
    {
      title: "Insulin resistance and reproductive outcomes in PMOS (ex PCOS)",
      journal: "Human Reproduction Update",
      year: 2021,
      evidenceLevel: "review",
    },
    {
      title:
        "Vitamin D and IVF outcomes: a systematic review and meta-analysis",
      journal: "Human Reproduction",
      year: 2022,
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(comprehensiveHealthPanel);
